import type { JSX } from 'react'
import { ANALYZER_README, stageLabel } from '../../analysis/draft'
import type { AnalysisController } from '../../analysis/useAnalysis'
import { checkHealth } from '../../api/serverStatus'
import type { AnalyzerStatus } from '../../api/serverStatus'
import { AnalysisProgress } from './AnalysisProgress'

export type AnalysisPanelProps = {
  analyzer: AnalyzerStatus
  serverOnline: boolean | null
  hasVideo: boolean
  controller: AnalysisController
  reviewOpen: boolean
  onOpenDialog: () => void
  onOpenReview: () => void
}

/** Почему кнопка заблокирована; null — всё в порядке. */
function blockedReason(
  analyzer: AnalyzerStatus,
  serverOnline: boolean | null,
  hasVideo: boolean,
  busy: boolean,
): string | null {
  if (serverOnline === false) return 'Бэкенд не отвечает'
  if (!hasVideo) return 'Сначала загрузите видео — разбирать нечего'
  if (!analyzer.known) return 'Бэкенд не сообщает о сервисе разбора'
  if (!analyzer.available) return 'Сервис разбора не запущен'
  if (busy) return 'Разбор уже идёт'
  return null
}

export function AnalysisPanel({
  analyzer,
  serverOnline,
  hasVideo,
  controller,
  reviewOpen,
  onOpenDialog,
  onOpenReview,
}: AnalysisPanelProps): JSX.Element {
  const { analysis, draft, starting, cancelling, fetchingResult, error } = controller
  const busy = analysis?.status === 'queued' || analysis?.status === 'running'
  const reason = blockedReason(analyzer, serverOnline, hasVideo, busy)
  const unavailable = !analyzer.available

  return (
    <section className="panel">
      <header>
        <h2>Автоматический разбор</h2>
        {analyzer.available ? (
          <>
            <span className="badge" style={{ color: 'var(--success)' }}>
              сервис на связи
            </span>
            {analyzer.device ? (
              <span
                className="badge mono"
                style={analyzer.device === 'cpu' ? { color: 'var(--warning)' } : undefined}
                title={analyzer.gpu || undefined}
              >
                {analyzer.device}
              </span>
            ) : null}
          </>
        ) : (
          <span className="badge" style={{ color: 'var(--warning)' }}>
            недоступен
          </span>
        )}
      </header>

      <div className="stack">
        {unavailable ? (
          <div className="notice warn">
            <strong>
              {analyzer.known
                ? 'Сервис разбора не отвечает'
                : 'Бэкенд не знает про автоматический разбор'}
            </strong>
            <span className="faint">
              {analyzer.known
                ? 'Это штатная ситуация, а не поломка: разбор — отдельный процесс на Python с моделями на несколько гигабайт, он запускается вручную и держит их в памяти видеокарты.'
                : 'В ответе /api/health нет сведений об анализаторе. Либо сервер собран до появления этой возможности, либо адрес сервиса ему не задан.'}
            </span>
            {analyzer.detail ? <span className="faint mono">{analyzer.detail}</span> : null}
            <span className="faint">
              Как поднять — <span className="mono">{ANALYZER_README}</span>. Всё остальное
              в редакторе работает как раньше: разметка, запись, рендер.
            </span>
            <div className="row">
              <button type="button" className="ghost small" onClick={() => void checkHealth()}>
                Проверить снова
              </button>
            </div>
          </div>
        ) : null}

        {analyzer.available && Object.keys(analyzer.models).length > 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            Модели:{' '}
            <span className="mono">
              {Object.entries(analyzer.models)
                .map(([stage, model]) => `${stageLabel(stage)} — ${model}`)
                .join(' · ')}
            </span>
          </p>
        ) : null}

        {analysis && busy ? (
          <AnalysisProgress
            analysis={analysis}
            cancelling={cancelling}
            onCancel={() => void controller.cancel()}
          />
        ) : null}

        {analysis?.status === 'error' ? (
          <div className="notice warn">
            <strong>Разбор не удался</strong>
            <span className="faint">{analysis.error || 'Сервис не объяснил причину'}</span>
            <span className="faint">
              В проекте ничего не изменилось. Частая причина — не хватило памяти видеокарты
              на длинном видео.
            </span>
          </div>
        ) : null}

        {analysis?.status === 'cancelled' ? (
          <p className="notice" style={{ margin: 0 }}>
            Разбор отменён. Проект не изменился.
          </p>
        ) : null}

        {analysis?.status === 'done' && !draft ? (
          <div className="row wrap">
            <span className="faint">
              {fetchingResult ? 'Загружаю результат…' : 'Разбор закончен, но результат не загружен.'}
            </span>
            {!fetchingResult ? (
              <button type="button" onClick={() => void controller.reloadResult()}>
                Загрузить результат
              </button>
            ) : null}
          </div>
        ) : null}

        {draft ? (
          <div className="notice ok">
            <strong>Разбор готов — это черновик</strong>
            <span className="faint">
              {draft.lines.length} реплик, {draft.roles.length} говорящих. Ничего не применено:
              проверьте таблицу и подтвердите вручную.
            </span>
            <div className="row">
              <button type="button" className="primary" onClick={onOpenReview}>
                {reviewOpen ? 'Таблица открыта выше' : 'Проверить результат'}
              </button>
            </div>
          </div>
        ) : null}

        {error ? <p className="notice warn">{error}</p> : null}

        {!draft && !busy ? (
          <div className="row wrap">
            <button
              type="button"
              className="primary"
              disabled={reason !== null || starting}
              onClick={onOpenDialog}
              title={reason ?? 'Разделить звук, найти говорящих, расшифровать и перевести'}
            >
              ⚙ Разобрать автоматически
            </button>
            <span className="faint">
              {reason ??
                'Из видео с одной дорожкой получится черновик проекта: M&E, реплики по говорящим, расшифровка и перевод.'}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  )
}
